import React, { createContext, useState, useContext, useEffect } from "react";
import AppContext from "../AppContext";

// Create Authentication Context
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const { setLoggedInCustomer } = useContext(AppContext);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on component mount
  useEffect(() => {
    const user = localStorage.getItem("currentUser");

    if (user && user !== "undefined") {
      try {
        setCurrentUser(JSON.parse(user));
      } catch (error) {
        console.log("Invalid user data in localStorage");
        localStorage.removeItem("currentUser");
      }
    }

    setLoading(false);
  }, []);

  // Signup Function
  const signup = (userData) => {
    try {
      // Get existing users from localStorage
      const existingUsers = JSON.parse(localStorage.getItem("users")) || [];

      // Check if email already exists
      const userExists = existingUsers.find(
        (user) => user.email === userData.email,
      );
      if (userExists) {
        return { success: false, message: "Email already registered!" };
      }

      // Add new user with unique ID
      const newUser = {
        id: Date.now().toString(),
        fullName: userData.fullName,
        email: userData.email,
        password: userData.password, // In real app, password should be hashed
        createdAt: new Date().toISOString(),
      };

      existingUsers.push(newUser);
      localStorage.setItem("users", JSON.stringify(existingUsers));

      // Auto login after signup
      const userWithoutPassword = { ...newUser };
      delete userWithoutPassword.password;
      setCurrentUser(userWithoutPassword);
      localStorage.setItem("currentUser", JSON.stringify(userWithoutPassword));

      return { success: true, message: "Account created successfully!" };
    } catch (error) {
      return { success: false, message: "Signup failed. Please try again." };
    }
  };

  // Login Function
  const login = (email, password) => {
    try {
      // Get all users from localStorage
      const existingUsers = JSON.parse(localStorage.getItem("users")) || [];

      // Find user with matching email and password
      const user = existingUsers.find(
        (u) => u.email === email && u.password === password,
      );

      if (!user) {
        return { success: false, message: "Invalid email or password!" };
      }

      // Remove password from user object before storing
      const userWithoutPassword = { ...user };
      delete userWithoutPassword.password;

      // Set current user
      setCurrentUser(userWithoutPassword);
      localStorage.setItem("currentUser", JSON.stringify(userWithoutPassword));

      return { success: true, message: "Login successful!" };
    } catch (error) {
      return { success: false, message: "Login failed. Please try again." };
    }
  };

  // Logout Function
  const logout = () => {
    setLoggedInCustomer(null);
    localStorage.removeItem("loggedInCustomer");
    setCurrentUser(null);
    localStorage.removeItem("currentUser");
    localStorage.removeItem("authToken");
    return { success: true, message: "Logged out successfully!" };
  };

  // Update User Profile
  const updateProfile = (updatedData) => {
    try {
      const users = JSON.parse(localStorage.getItem("users")) || [];
      const userIndex = users.findIndex((u) => u.id === currentUser.id);

      if (userIndex !== -1) {
        users[userIndex] = { ...users[userIndex], ...updatedData };
        localStorage.setItem("users", JSON.stringify(users));

        const updatedUser = { ...users[userIndex] };
        delete updatedUser.password;
        setCurrentUser(updatedUser);
        localStorage.setItem("currentUser", JSON.stringify(updatedUser));

        return { success: true, message: "Profile updated successfully!" };
      }

      return { success: false, message: "User not found!" };
    } catch (error) {
      return { success: false, message: "Update failed. Please try again." };
    }
  };

  // Get all registered users (for testing/admin purposes)
  const getAllUsers = () => {
    return JSON.parse(localStorage.getItem("users")) || [];
  };

  const value = {
    currentUser,
    loading,
    signup,
    login,
    logout,
    updateProfile,
    getAllUsers,
    isAuthenticated: !!currentUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
